import * as THREE from 'three';
import { TRIPOD_MASK_CONFIG } from '../utils/Constants.js';

/**
 * Manages the tripod mask overlay
 */
export class TripodMaskManager {
    constructor(scene, textureLoader) {
        this.scene = scene;
        this.textureLoader = textureLoader;
        
        this.mask = null;
        this.maskTexture = null;
        this.maskSize = TRIPOD_MASK_CONFIG.DEFAULT_SIZE;
    }

    /**
     * Load and display a tripod mask
     * @param {File} file - Mask image file
     */
    async loadMask(file) {
        const url = URL.createObjectURL(file);
        const texture = await this.textureLoader.loadStandardTexture(url);

        this.maskTexture = texture;

        // Remove existing mask if any
        this.removeMask();

        // Create new mask
        this.createMask();

        console.log(`✅ Tripod mask loaded: ${file.name}`);
        return texture;
    }

    /**
     * Load and display a tripod mask from URL (for API data)
     * @param {string} url - Mask image URL
     * @param {number|null} version - Version timestamp for cache-busting (optional)
     */
    async loadMaskFromURL(url, version = null) {
        // Add version parameter for cache-busting if provided
        const finalUrl = version ? `${url}?v=${version}` : url;

        const texture = await this.textureLoader.loadStandardTexture(finalUrl);

        this.maskTexture = texture;

        // Remove existing mask if any
        this.removeMask();

        // Create new mask
        this.createMask();

        console.log(`✅ Tripod mask loaded from URL: ${finalUrl}`);
        return texture;
    }

    /**
     * Create the tripod mask mesh
     */
    createMask() {
        if (!this.maskTexture) return;

        const size = TRIPOD_MASK_CONFIG.BASE_GEOMETRY_SIZE * this.maskSize;
        const geometry = new THREE.PlaneGeometry(size, size);

        const material = new THREE.MeshBasicMaterial({
            map: this.maskTexture,
            transparent: true,
            opacity: 1.0,
            side: THREE.DoubleSide
        });

        this.mask = new THREE.Mesh(geometry, material);

        // Position at bottom of sphere
        this.mask.position.set(0, TRIPOD_MASK_CONFIG.POSITION_Y, 0);
        this.mask.rotation.x = TRIPOD_MASK_CONFIG.ROTATION_X;

        this.scene.add(this.mask);
        console.log('🎭 Tripod mask created with size:', this.maskSize);
    }

    /**
     * Update the size of the tripod mask
     * @param {number} size - Size multiplier
     */
    setSize(size) {
        this.maskSize = size;
        
        if (this.mask) {
            const newSize = TRIPOD_MASK_CONFIG.BASE_GEOMETRY_SIZE * this.maskSize;
            this.mask.geometry.dispose();
            this.mask.geometry = new THREE.PlaneGeometry(newSize, newSize);
            console.log('🎭 Tripod mask size updated to:', this.maskSize);
        }
    }

    /**
     * Remove the tripod mask
     */
    removeMask() {
        if (this.mask) {
            this.scene.remove(this.mask);
            this.mask.geometry.dispose();
            this.mask.material.dispose();
            this.mask = null;
        }
    }

    /**
     * Ensure mask is visible in the scene (useful after transitions)
     */
    ensureVisible() {
        if (this.mask && !this.scene.children.includes(this.mask)) {
            this.scene.add(this.mask);
        }
    }

    /**
     * Check if mask is loaded
     * @returns {boolean}
     */
    hasMask() {
        return this.mask !== null;
    }

    /**
     * Cleanup and dispose all resources
     */
    dispose() {
        console.log('[TripodMaskManager] Disposing...');

        // Remove mask
        this.removeMask();

        // Dispose texture
        if (this.maskTexture) {
            this.maskTexture.dispose();
            this.maskTexture = null;
        }

        console.log('[TripodMaskManager] Disposed successfully');
    }
}

